import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { errMsg, useApi, useTaxonomy } from "../../lib/api";
import { ErrorBox, Field, Loading } from "../../components/kit";

function CheckGroup({ legend, items, value, onChange, disabled, prefix }: any) {
  const toggle = (id: string) => onChange(value.includes(id) ? value.filter((x: string) => x !== id) : [...value, id]);
  return (
    <fieldset>
      <legend className="label">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {items.map((it: any) => (
          <label key={it.id} className="flex items-center gap-2 rounded border border-[var(--steel)] bg-white px-2 py-1 text-sm">
            <input type="checkbox" disabled={disabled} checked={value.includes(it.id)} onChange={() => toggle(it.id)} data-testid={`${prefix}-${it.id}-checkbox`} />{it.name}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function VendorProfileForm({ org, isOwner }: { org: any; isOwner: boolean }) {
  const api = useApi();
  const { categories, areas } = useTaxonomy();
  const [v, setV] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (org.data?.profile) {
      setV(org.data.profile);
    }
  }, [org.data]);
  if (org.loading && !v) return <Loading />;
  if (org.error) return <ErrorBox message={org.error} onRetry={org.reload} />;
  if (!v) return null;
  const set = (k: string, val: any) => setV((s: any) => ({ ...s, [k]: val }));
  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { display_name, headline, description, category_ids, area_ids, availability } = v;
      await api("put", "/vendor/profile", { display_name, headline, description, category_ids, area_ids, availability });
      toast.success("Profile saved"); org.reload();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="card max-w-3xl space-y-5" data-testid="vendor-profile-form">
      {!isOwner && <p className="text-sm text-[var(--muted)]" data-testid="vendor-profile-readonly-notice">Only the organization owner can edit the profile.</p>}
      <Field label="Display name" id="vp-name"><input id="vp-name" className="input" disabled={!isOwner} value={v.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="vendor-profile-name-input" /></Field>
      <Field label="Headline" id="vp-headline"><input id="vp-headline" className="input" disabled={!isOwner} value={v.headline} onChange={(e) => set("headline", e.target.value)} data-testid="vendor-profile-headline-input" /></Field>
      <Field label="Description" id="vp-desc"><textarea id="vp-desc" rows={4} className="input" disabled={!isOwner} value={v.description} onChange={(e) => set("description", e.target.value)} data-testid="vendor-profile-description-input" /></Field>
      <CheckGroup legend="Trades" prefix="vendor-profile-category" items={categories} value={v.category_ids} onChange={(x: any) => set("category_ids", x)} disabled={!isOwner} />
      <CheckGroup legend="Service areas" prefix="vendor-profile-area" items={areas} value={v.area_ids} onChange={(x: any) => set("area_ids", x)} disabled={!isOwner} />
      <Field label="Availability" id="vp-avail" hint="Unavailable removes you from new organic matches.">
        <select id="vp-avail" className="input" disabled={!isOwner} value={v.availability} onChange={(e) => set("availability", e.target.value)} data-testid="vendor-profile-availability-select">
          <option value="available">Available</option><option value="limited">Limited</option><option value="unavailable">Unavailable</option>
        </select>
      </Field>
      {isOwner && <button className="btn btn-primary" disabled={busy} data-testid="vendor-profile-save-button">{busy ? "Saving…" : "Save profile"}</button>}
    </form>
  );
}
